import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import { Structure } from 'api';
import { customScroll } from 'styles/globalStyle';

const SList = styled.ul<{ top: number; left: number }>`
    list-style: none;
    position: fixed;
    background-color: ${({ theme }) => theme.colors.white};
    left: ${({ left }) => left}px;
    top: ${({ top }) => top}px;
    padding: 4px;
    min-width: 200px;
    z-index: ${({ theme }) => theme.z_index.dropdown};
    box-shadow: ${({ theme }) => theme.shadow.ellipsis};
    border-radius: 4px;
    max-height: 300px;
    overflow-y: auto;
    margin: 0;
    ${customScroll};
`;

const SListItem = styled.li`
    text-align: left;
`;

const SButton = styled.button<{ isActive: boolean }>`
    background-color: ${({ isActive, theme }) =>
        isActive ? theme.colors.primary_40 : 'transparent'};
    border: none;
    cursor: pointer;
    font-size: ${({ theme }) => theme.font.size.small};
    width: 100%;
    padding: 8px;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.black};
    border-radius: 5px;
    text-decoration: none;
    display: block;
    text-align: left;

    &:hover {
        background-color: ${({ theme }) => theme.colors.highlight};
    }
`;

interface Props {
    storeys: Structure[];
    currentStorey: Structure;
    onClose: () => void;
    onSelect: (storey: Structure) => void;
    buttonRef: React.RefObject<HTMLButtonElement>;
}

export const StoreyDropdownMenu = ({ storeys, currentStorey, onClose, onSelect, buttonRef }: Props) => {
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

    const calculatePosition = useCallback(() => {
        if (!buttonRef.current) return;

        const buttonRect = buttonRef.current.getBoundingClientRect();
        setMenuPosition({
            top: buttonRect.bottom + 8,
            left: buttonRect.left,
        });
    }, [buttonRef]);

    useEffect(() => {
        calculatePosition();

        window.addEventListener('scroll', calculatePosition, true);
        window.addEventListener('resize', calculatePosition);

        return () => {
            window.removeEventListener('scroll', calculatePosition, true);
            window.removeEventListener('resize', calculatePosition);
        };
    }, [calculatePosition]);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>, storey: Structure) => {
        e.preventDefault();
        e.stopPropagation();
        onSelect(storey);
        onClose();
    };

    return createPortal(
        <SList
            className="storey-dropdown-menu"
            top={menuPosition.top}
            left={menuPosition.left}
            onClick={(e) => e.stopPropagation()}
        >
            {storeys.map((storey) => (
                <SListItem key={storey.id}>
                    <SButton
                        isActive={storey.id === currentStorey.id}
                        onClick={(e) => handleClick(e, storey)}
                        disabled={storey.id === currentStorey.id}
                    >
                        {storey.name}
                    </SButton>
                </SListItem>
            ))}
        </SList>,
        document.body
    );
};

